// frontend/src/components/aria/AriaActionCard.js
import { useState } from 'react';
import { writeHandoff } from '../../lib/workflowState';
import api from '../../api';

const COLORS = {
  navigate: 'hsl(200,80%,55%)',
  create: 'hsl(75,100%,50%)',
  update: 'hsl(12,90%,55%)',
};

const S = {
  card: (type) => ({
    border: `1px solid ${COLORS[type] || 'hsl(0,0%,25%)'}55`,
    background: 'hsl(0,0%,4%)',
    marginTop: 8, overflow: 'hidden',
  }),
  header: (type) => ({
    padding: '6px 10px',
    background: `${COLORS[type] || 'hsl(0,0%,15%)'}22`,
    borderBottom: `1px solid ${COLORS[type] || 'hsl(0,0%,20%)'}33`,
    display: 'flex', alignItems: 'center', gap: 6,
  }),
  headerText: (type) => ({
    color: COLORS[type] || 'hsl(0,0%,60%)',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700,
  }),
  body: { padding: '8px 10px' },
  label: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 10, color: 'hsl(0,0%,70%)', marginBottom: 8, lineHeight: 1.5,
  },
  btnRow: { display: 'flex', gap: 6, marginTop: 8 },
  btn: (primary, color) => ({
    padding: '4px 12px', fontSize: 9, cursor: 'pointer',
    fontFamily: "'JetBrains Mono', monospace",
    textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700,
    background: primary ? color + '33' : 'transparent',
    border: `1px solid ${primary ? color : 'hsl(0,0%,20%)'}`,
    color: primary ? color : 'hsl(0,0%,40%)',
  }),
  result: (ok) => ({
    padding: '6px 10px',
    fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
    color: ok ? 'hsl(142,70%,55%)' : 'hsl(0,84%,60%)',
    background: ok ? 'hsl(142,60%,5%)' : 'hsl(0,60%,5%)',
    border: `1px solid ${ok ? 'hsl(142,60%,20%)' : 'hsl(0,60%,20%)'}`,
    marginTop: 8,
  }),
};

/**
 * AriaActionCard — renders a single proposed action with Confirm/Skip buttons.
 *
 * Props:
 *   action         AriaAction { type, label, target_path, payload }
 *   index          number (1-based)
 *   total          number
 *   onSkip         () => void
 *   onConfirm      () => void
 *   requireConfirm boolean  If false, auto-executes on mount
 */
export default function AriaActionCard({ action, index, total, onSkip, onConfirm, requireConfirm = true }) {
  const [status, setStatus] = useState('idle'); // 'idle'|'loading'|'done'|'error'
  const [errorMsg, setErrorMsg] = useState('');
  const color = COLORS[action.type] || 'hsl(0,0%,40%)';

  const execute = async () => {
    setStatus('loading');
    try {
      if (action.type === 'navigate') {
        const path = action.target_path || '/';
        const params = new URLSearchParams();
        if (action.payload?.highlight) params.set('highlight', action.payload.highlight);
        if (action.payload?.from_dispatch) params.set('from_dispatch', action.payload.from_dispatch);
        const qs = params.toString();
        const url = qs ? `${path}?${qs}` : path;
        window.open(url, '_blank');
        setStatus('done');
        onConfirm?.();
      } else if (action.type === 'create') {
        const key = writeHandoff(action.payload || {});
        window.open(`${action.target_path}?action_key=${key}`, '_blank');
        setStatus('done');
        onConfirm?.();
      } else if (action.type === 'update') {
        const { endpoint, body } = action.payload || {};
        if (!endpoint) throw new Error('No endpoint specified');
        await api.put(endpoint, body || {});
        setStatus('done');
        onConfirm?.();
      } else {
        throw new Error(`Unknown action type: ${action.type}`);
      }
    } catch (e) {
      setStatus('error');
      setErrorMsg(e.message);
    }
  };

  if (!requireConfirm && status === 'idle') {
    execute();
    return null;
  }

  if (status === 'done') {
    return <div style={S.result(true)}>✓ {action.label} — done</div>;
  }

  return (
    <div style={S.card(action.type)} data-testid="aria-action-card">
      <div style={S.header(action.type)}>
        <span style={S.headerText(action.type)}>
          Action {index} of {total} · {action.type.toUpperCase()}
        </span>
      </div>
      <div style={S.body}>
        <div style={S.label}>{action.label}</div>
        {status === 'error' && <div style={S.result(false)}>Error: {errorMsg}</div>}
        {status !== 'error' && (
          <div style={S.btnRow}>
            <button
              data-testid="aria-action-confirm"
              style={S.btn(true, color)}
              onClick={execute}
              disabled={status === 'loading'}
            >
              {status === 'loading' ? 'Working...' : 'Confirm'}
            </button>
            <button data-testid="aria-action-skip" style={S.btn(false, color)} onClick={onSkip}>
              Skip
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
